import {
  useEffect,
  useRef,
  useState,
} from "react";
import {
  apiRequest,
} from "../../shared/api/client";
import {
  type SessionUser,
} from "../../shared/auth/access";

type SupplierRow = {
  id?: string;
  name: string;
  category: string;
  categories?: string[];
  score: string;
  delivery: string;
  orders: number;
  volume: string;
  risk: string;
  legalName?: string;
  countryCode?: string | null;
  taxNumber?: string | null;
  status?: string;
  version?: number;
  capabilities?: Array<{
    category: string;
    productGroupCode: string;
    productGroupName: string;
  }>;
  contacts?: Array<{
    id?: string;
    fullName: string;
    role: string;
    priority: string;
    email: string;
    emailVerified?: boolean;
  }>;
};

export type SuppliersModuleProps = {
  notify: (message: string) => void;
  user: SessionUser;
  initialQuery?: string;
  navigationVersion: number;
  dataRefreshVersion: number;
};

export function useSuppliersWorkspace({
  notify,
  user,
  initialQuery,
  navigationVersion,
  dataRefreshVersion,
}: SuppliersModuleProps) {
  const [rows, setRows] = useState<SupplierRow[]>([]);
  const [query, setQuery] = useState("");
  const [riskFilter, setRiskFilter] = useState("Tümü");
  const [composerOpen, setComposerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedSupplierId, setSelectedSupplierId] = useState<string | null>(
    null,
  );
  const supplierModalOpenerRef = useRef<HTMLElement | null>(null);
  const [supplierTab, setSupplierTab] = useState<
    "overview" | "contacts" | "quality" | "documents" | "history"
  >("overview");
  const [supplierAction, setSupplierAction] = useState<
    "status" | "contact" | "quality" | "document" | null
  >(null);
  const [supplierActionPending, setSupplierActionPending] = useState(false);
  const [nextStatus, setNextStatus] = useState("UNDER_REVIEW");
  const [contactForm, setContactForm] = useState({
    fullName: "",
    email: "",
    role: "RFQ",
    priority: "PRIMARY",
  });
  const [qualityScope, setQualityScope] = useState({
    siteCode: "",
    category: "Ambalaj",
    productGroupCode: "",
    documentTypeCode: "",
    documentTypeName: "",
    validUntil: "",
    conditionalLimit: "",
    conditionalUnit: "KG",
  });
  const [qualityDocumentTypes, setQualityDocumentTypes] = useState<
    Array<{ code: string; name: string; category: string; }>
  >([]);
  const [qualityDocumentFile, setQualityDocumentFile] = useState<File | null>(
    null,
  );
  const [qualityDocuments, setQualityDocuments] = useState<
    Array<{
      id: string;
      document_type_code?: string;
      documentTypeCode?: string;
      version: number;
      original_name?: string;
      originalName?: string;
      security_status?: string;
      securityStatus?: string;
      verification_status?: string;
      verificationStatus?: string;
      valid_until?: string;
      validUntil?: string;
    }>
  >([]);
  const [eligibility, setEligibility] = useState<{
    eligible: boolean;
    qualificationStatus: string;
    blockingReasons: string[];
    evaluatedAt: string;
  } | null>(null);
  const [supplierGovernance, setSupplierGovernance] = useState<{
    activeContracts: number;
    remainingContractLimit: number;
    openCapas: number;
    expiringDocuments: number;
    orderCount: number;
    onTimeRate: number | null;
  } | null>(null);
  const [qualificationCase, setQualificationCase] = useState<{
    id: string;
    version: number;
    status: string;
    decision_history?: Array<{
      id: string;
      decision: string;
      decision_version: number;
      reason: string;
      decided_at: string;
    }>;
    decisionHistory?: Array<{
      id: string;
      status: string;
      reason: string;
      effectiveFrom: string;
    }>;
  } | null>(null);
  const [form, setForm] = useState({
    name: "",
    code: "",
    category: "",
    taxNumber: "",
    legalName: "",
    countryCode: "TR",
    addressLine: "",
    productGroupCode: "",
    productGroupName: "",
    contactName: "",
    contactEmail: "",
    contactRole: "RFQ",
    includeOtherCategory: false,
  });
  useEffect(() => {
    if (initialQuery) setQuery(initialQuery);
  }, [initialQuery, navigationVersion]);
  useEffect(() => {
    apiRequest<SupplierRow[]>("/api/suppliers")
      .then(setRows)
      .catch((error) =>
        notify(
          error instanceof Error
            ? error.message
            : "Tedarikçiler sunucudan alınamadı.",
        ),
      )
      .finally(() => setLoading(false));
  }, [dataRefreshVersion, notify]);
  const normalizedQuery = query.trim().toLocaleLowerCase("tr");
  const visibleRows = rows.filter((supplier) => {
    const searchableValue = [
      supplier.name,
      supplier.legalName,
      supplier.category,
      ...(supplier.capabilities?.flatMap((item) => [
        item.productGroupName,
        item.productGroupCode,
      ]) ?? []),
    ]
      .filter(Boolean)
      .join(" ")
      .toLocaleLowerCase("tr");
    const normalizedRisk = supplier.risk.toLocaleLowerCase("tr");
    const riskMatches =
      riskFilter === "Tümü" ||
      normalizedRisk.startsWith(riskFilter.toLocaleLowerCase("tr"));
    return searchableValue.includes(normalizedQuery) && riskMatches;
  });
  const suppliersWithoutPrimaryContact = rows.filter(
    (supplier) =>
      !supplier.contacts?.some((contact) => contact.priority === "PRIMARY"),
  ).length;
  const suppliersNeedingRiskFollowUp = rows.filter(
    (supplier) => !supplier.risk.toLocaleLowerCase("tr").startsWith("düşük"),
  ).length;
  const selectedSupplier =
    rows.find((supplier) => supplier.id === selectedSupplierId) ?? null;
  const supplierStatusLabels: Record<string, string> = {
    DRAFT: "Taslak",
    UNDER_REVIEW: "İncelemede",
    ACTIVE: "Aktif",
    SUSPENDED: "Askıda",
    BLOCKED: "Engelli",
    ARCHIVED: "Arşivlendi",
  };
  const qualificationLabels: Record<string, string> = {
    NOT_EVALUATED: "Değerlendirilmedi",
    UNDER_REVIEW: "İncelemede",
    APPROVED: "Onaylandı",
    CONDITIONAL: "Şartlı onay",
    REJECTED: "Reddedildi",
    SUSPENDED: "Askıda",
    EXPIRED: "Süresi doldu",
  };
  const contactRoleLabels: Record<string, string> = {
    RFQ: "Teklif",
    ORDER: "Sipariş",
    QUALITY: "Kalite",
    CONTRACT: "Sözleşme",
  };
  const contactRoleDescriptions: Record<string, string> = {
    RFQ: "Teklif talebi, fiyat ve revizyon yazışmaları bu kişiye yönlendirilir.",
    ORDER: "Sipariş, sevkiyat ve teslimat yazışmaları bu kişiye yönlendirilir.",
    QUALITY:
      "Kalite belgeleri, uygunsuzluklar ve DÖF yazışmaları bu kişiye yönlendirilir.",
    CONTRACT:
      "Sözleşme hazırlama ve imza süreci yazışmaları bu kişiye yönlendirilir.",
  };
  const priorityLabels: Record<string, string> = {
    PRIMARY: "Birincil",
    BACKUP: "Yedek",
  };
  const blockingReasonLabels: Record<string, string> = {
    CONDITION_MAX_QUANTITY_NOT_MET: "Şartlı onayın miktar sınırı aşılıyor",
    MANDATORY_DOCUMENT_MISSING: "Zorunlu belge eksik",
    CRITICAL_CAPA_OPEN: "Kritik düzeltici faaliyet açık",
    QUALIFICATION_NOT_APPROVED: "Resmî kalite onayı yok",
  };
  const documentSecurityLabels: Record<string, string> = {
    PENDING: "Güvenlik kontrolü bekliyor",
    CLEAN: "Güvenli",
    BLOCKED: "Engellendi",
    FAILED: "Kontrol başarısız",
  };
  const documentVerificationLabels: Record<string, string> = {
    PENDING: "Kalite doğrulaması bekliyor",
    VERIFIED: "Doğrulandı",
    REJECTED: "Reddedildi",
  };
  useEffect(() => {
    if (!selectedSupplier) return;
    const app = document.querySelector<HTMLElement>(".app-shell");
    document.body.classList.add("procurement-modal-open");
    app?.setAttribute("inert", "");
    app?.setAttribute("aria-hidden", "true");
    requestAnimationFrame(() =>
      document
        .querySelector<HTMLElement>(".supplier-workspace .icon-button")
        ?.focus(),
    );
    return () => {
      document.body.classList.remove("procurement-modal-open");
      app?.removeAttribute("inert");
      app?.removeAttribute("aria-hidden");
      supplierModalOpenerRef.current?.focus();
    };
  }, [selectedSupplier]);
  useEffect(() => {
    if (!selectedSupplierId) return;
    apiRequest<Array<{ code: string; name: string; category: string; }>>(
      "/api/supplier-document-types",
    )
      .then(setQualityDocumentTypes)
      .catch(() => setQualityDocumentTypes([]));
  }, [selectedSupplierId]);
  useEffect(() => {
    if (!selectedSupplierId) {
      setSupplierGovernance(null);
      return;
    }
    apiRequest<{
      activeContracts: number;
      remainingContractLimit: number;
      openCapas: number;
      expiringDocuments: number;
      orderCount: number;
      onTimeRate: number | null;
    }>(`/api/suppliers/${selectedSupplierId}/governance-summary`)
      .then(setSupplierGovernance)
      .catch(() => setSupplierGovernance(null));
  }, [selectedSupplierId]);
  const addContact = async () => {
    if (
      !selectedSupplier?.id ||
      !contactForm.fullName.trim() ||
      !contactForm.email.trim()
    ) {
      notify("İletişim kişisi adı ve e-postası zorunludur.");
      return;
    }
    setSupplierActionPending(true);
    try {
      const contact = await apiRequest<
        NonNullable<SupplierRow["contacts"]>[number]
      >(`/api/suppliers/${selectedSupplier.id}/contacts`, {
        method: "POST",
        body: JSON.stringify(contactForm),
      });
      setRows((current) =>
        current.map((supplier) =>
          supplier.id === selectedSupplier.id
            ? { ...supplier, contacts: [...(supplier.contacts ?? []), contact] }
            : supplier,
        ),
      );
      setContactForm({
        fullName: "",
        email: "",
        role: "RFQ",
        priority: "PRIMARY",
      });
      setSupplierAction(null);
      notify("İletişim kişisi eklendi.");
    } catch (error) {
      notify(
        error instanceof Error ? error.message : "İletişim kişisi eklenemedi.",
      );
    } finally {
      setSupplierActionPending(false);
    }
  };
  const verifyContact = async (contactId?: string) => {
    if (!selectedSupplier?.id || !contactId) return;
    setSupplierActionPending(true);
    try {
      await apiRequest(
        `/api/suppliers/${selectedSupplier.id}/contacts/${contactId}/verify`,
        {
          method: "POST",
          body: JSON.stringify({
            verificationReference: "INTERNAL_AUTHORIZED_REVIEW",
          }),
        },
      );
      setRows((current) =>
        current.map((supplier) =>
          supplier.id === selectedSupplier.id
            ? {
              ...supplier,
              contacts: supplier.contacts?.map((contact) =>
                contact.id === contactId
                  ? { ...contact, emailVerified: true }
                  : contact,
              ),
            }
            : supplier,
        ),
      );
      notify("E-posta sunucu tarafından doğrulandı.");
    } catch (error) {
      notify(error instanceof Error ? error.message : "E-posta doğrulanamadı.");
    } finally {
      setSupplierActionPending(false);
    }
  };
  const changeSupplierStatus = async () => {
    if (!selectedSupplier?.id) return;
    setSupplierActionPending(true);
    try {
      const updated = await apiRequest<{ status: string; version: number; }>(
        `/api/suppliers/${selectedSupplier.id}/status`,
        {
          method: "PUT",
          body: JSON.stringify({
            status: nextStatus,
            expectedVersion: selectedSupplier.version ?? 1,
          }),
        },
      );
      setRows((current) =>
        current.map((supplier) =>
          supplier.id === selectedSupplier.id
            ? { ...supplier, ...updated }
            : supplier,
        ),
      );
      setSupplierAction(null);
      notify("Tedarikçi durumu güncellendi.");
    } catch (error) {
      notify(
        error instanceof Error
          ? error.message
          : "Tedarikçi durumu güncellenemedi.",
      );
    } finally {
      setSupplierActionPending(false);
    }
  };
  const checkEligibility = async () => {
    if (
      !selectedSupplier?.id ||
      !qualityScope.siteCode.trim() ||
      !qualityScope.productGroupCode.trim()
    ) {
      notify("Tesis ve ürün grubu kapsamı zorunludur.");
      return;
    }
    setSupplierActionPending(true);
    try {
      setEligibility(
        await apiRequest(
          `/api/suppliers/${selectedSupplier.id}/eligibility?site=${encodeURIComponent(qualityScope.siteCode)}&category=${encodeURIComponent(qualityScope.category)}&productGroup=${encodeURIComponent(qualityScope.productGroupCode)}`,
        ),
      );
    } catch (error) {
      notify(
        error instanceof Error ? error.message : "Kalite uygunluğu alınamadı.",
      );
    } finally {
      setSupplierActionPending(false);
    }
  };
  const assignCapabilitySite = async () => {
    if (
      !selectedSupplier?.id ||
      !qualityScope.siteCode ||
      !qualityScope.productGroupCode
    ) {
      notify("Tesis ve ürün grubu zorunludur.");
      return;
    }
    try {
      await apiRequest(
        `/api/suppliers/${selectedSupplier.id}/capability-sites`,
        {
          method: "POST",
          headers: { "Idempotency-Key": crypto.randomUUID() },
          body: JSON.stringify({
            siteCode: qualityScope.siteCode,
            category: qualityScope.category,
            productGroupCode: qualityScope.productGroupCode,
            expectedVersion: 0,
          }),
        },
      );
      notify("Tedarikçi tesis kapsamı kaydedildi.");
    } catch (error) {
      notify(
        error instanceof Error ? error.message : "Tesis kapsamı kaydedilemedi.",
      );
    }
  };
  const createQualityCase = async () => {
    if (!selectedSupplier?.id) return;
    setSupplierActionPending(true);
    try {
      const created = await apiRequest<{
        id: string;
        version: number;
        status: string;
      }>(`/api/suppliers/${selectedSupplier.id}/qualification-cases`, {
        method: "POST",
        headers: { "Idempotency-Key": crypto.randomUUID() },
        body: JSON.stringify({
          siteCode: qualityScope.siteCode,
          category: qualityScope.category,
          productGroupCode: qualityScope.productGroupCode,
          checklistVersion: "v1",
          checklistEvidence: [
            { item: "Kimlik ve kapsam incelemesi", result: "COMPLETED" },
          ],
          sampleEvidenceRefs: [],
          documentEvidenceRefs: qualityDocuments
            .filter(
              (item) =>
                (item.verification_status ?? item.verificationStatus) ===
                "VERIFIED",
            )
            .map((item) => item.id),
        }),
      });
      setQualificationCase(created);
      notify("Kalite değerlendirme dosyası oluşturuldu.");
    } catch (error) {
      notify(
        error instanceof Error
          ? error.message
          : "Kalite dosyası oluşturulamadı.",
      );
    } finally {
      setSupplierActionPending(false);
    }
  };
  const loadQualityCase = async () => {
    if (!selectedSupplier?.id) return;
    setSupplierActionPending(true);
    try {
      setQualificationCase(
        await apiRequest(
          `/api/suppliers/${selectedSupplier.id}/qualification-cases?site=${encodeURIComponent(qualityScope.siteCode)}&category=${encodeURIComponent(qualityScope.category)}&productGroup=${encodeURIComponent(qualityScope.productGroupCode)}`,
        ),
      );
    } catch (error) {
      notify(
        error instanceof Error ? error.message : "Kalite dosyası bulunamadı.",
      );
    } finally {
      setSupplierActionPending(false);
    }
  };
  const submitQualityCase = async () => {
    if (!qualificationCase) return;
    setSupplierActionPending(true);
    try {
      const updated = await apiRequest<{
        id: string;
        version: number;
        status: string;
      }>(`/api/supplier-qualification-cases/${qualificationCase.id}/submit`, {
        method: "POST",
        headers: { "Idempotency-Key": crypto.randomUUID() },
        body: JSON.stringify({ expectedVersion: qualificationCase.version }),
      });
      setQualificationCase(updated);
      notify("Kalite dosyası karara gönderildi.");
    } catch (error) {
      notify(
        error instanceof Error
          ? error.message
          : "Kalite dosyası gönderilemedi.",
      );
    } finally {
      setSupplierActionPending(false);
    }
  };
  const decideQualityCase = async (
    decision: "APPROVED" | "CONDITIONAL" | "REJECTED",
  ) => {
    if (!qualificationCase) return;
    if (
      decision === "CONDITIONAL" &&
      (!qualityScope.validUntil || !Number(qualityScope.conditionalLimit))
    ) {
      notify("Şartlı onay için bitiş tarihi ve azami miktar zorunludur.");
      return;
    }
    setSupplierActionPending(true);
    try {
      await apiRequest(
        `/api/supplier-qualification-cases/${qualificationCase.id}/decision`,
        {
          method: "POST",
          headers: { "Idempotency-Key": crypto.randomUUID() },
          body: JSON.stringify({
            decision,
            reason:
              decision === "APPROVED"
                ? "Kalite kanıtları ve zorunlu belgeler uygun bulundu."
                : decision === "CONDITIONAL"
                  ? "Yapılandırılmış miktar koşuluyla kalite onayı verildi."
                  : "Kalite kanıtları yeterli bulunmadığı için reddedildi.",
            effectiveUntil:
              decision === "CONDITIONAL" ? qualityScope.validUntil : undefined,
            conditions:
              decision === "CONDITIONAL"
                ? [
                  {
                    type: "MAX_QUANTITY",
                    value: Number(qualityScope.conditionalLimit),
                    unit: qualityScope.conditionalUnit,
                    violationBehavior: "BLOCK",
                  },
                ]
                : [],
            evidenceRefs: qualityDocuments
              .filter(
                (item) =>
                  (item.verification_status ?? item.verificationStatus) ===
                  "VERIFIED",
              )
              .map((item) => item.id),
            expectedVersion: qualificationCase.version,
          }),
        },
      );
      notify("Resmî kalite kararı kaydedildi.");
      await loadQualityCase();
      await checkEligibility();
    } catch (error) {
      notify(
        error instanceof Error ? error.message : "Kalite kararı kaydedilemedi.",
      );
    } finally {
      setSupplierActionPending(false);
    }
  };
  const loadQualityDocuments = async () => {
    if (!selectedSupplier?.id) return;
    try {
      setQualityDocuments(
        await apiRequest(`/api/suppliers/${selectedSupplier.id}/documents`),
      );
    } catch (error) {
      notify(
        error instanceof Error ? error.message : "Kalite belgeleri alınamadı.",
      );
    }
  };
  const uploadQualityDocument = async () => {
    if (!selectedSupplier?.id || !qualityDocumentFile) {
      notify("Yüklenecek kalite belgesini seçin.");
      return;
    }
    const body = new FormData();
    body.set("file", qualityDocumentFile);
    body.set("documentTypeCode", qualityScope.documentTypeCode);
    if (qualityScope.validUntil)
      body.set("validUntil", qualityScope.validUntil);
    setSupplierActionPending(true);
    try {
      await apiRequest(`/api/suppliers/${selectedSupplier.id}/documents`, {
        method: "POST",
        headers: { "Idempotency-Key": crypto.randomUUID() },
        body,
      });
      setQualityDocumentFile(null);
      setSupplierAction(null);
      await loadQualityDocuments();
      notify("Belge güvenlik kontrolünden geçirilerek kaydedildi.");
    } catch (error) {
      notify(error instanceof Error ? error.message : "Belge yüklenemedi.");
    } finally {
      setSupplierActionPending(false);
    }
  };
  const verifyQualityDocument = async (id: string) => {
    setSupplierActionPending(true);
    try {
      await apiRequest(`/api/supplier-documents/${id}/verify`, {
        method: "POST",
        headers: { "Idempotency-Key": crypto.randomUUID() },
        body: JSON.stringify({
          decision: "VERIFIED",
          reason: "Yetkili kalite incelemesi tamamlandı.",
        }),
      });
      await loadQualityDocuments();
      notify("Belge doğrulandı.");
    } catch (error) {
      notify(error instanceof Error ? error.message : "Belge doğrulanamadı.");
    } finally {
      setSupplierActionPending(false);
    }
  };
  const suspendQualityCase = async () => {
    if (!qualificationCase) return;
    setSupplierActionPending(true);
    try {
      await apiRequest(
        `/api/supplier-qualification-cases/${qualificationCase.id}/suspend`,
        {
          method: "POST",
          headers: { "Idempotency-Key": crypto.randomUUID() },
          body: JSON.stringify({
            reason:
              "Kalite uygunluğu yetkili yönetici tarafından askıya alındı.",
            expectedVersion: qualificationCase.version,
          }),
        },
      );
      await loadQualityCase();
      await checkEligibility();
      notify("Kalite uygunluğu askıya alındı.");
    } catch (error) {
      notify(
        error instanceof Error ? error.message : "Askıya alma tamamlanamadı.",
      );
    } finally {
      setSupplierActionPending(false);
    }
  };
  const createQualityDocumentType = async () => {
    if (
      !qualityScope.documentTypeCode.trim() ||
      !qualityScope.documentTypeName.trim()
    ) {
      notify("Belge türü kodu ve adı zorunludur.");
      return;
    }
    try {
      const created = await apiRequest<{
        code: string;
        name: string;
        category: string;
      }>("/api/supplier-document-types", {
        method: "POST",
        headers: { "Idempotency-Key": crypto.randomUUID() },
        body: JSON.stringify({
          code: qualityScope.documentTypeCode,
          name: qualityScope.documentTypeName,
          category: qualityScope.category,
          productGroupCode: qualityScope.productGroupCode || undefined,
          mandatory: true,
          expectedVersion: 0,
        }),
      });
      setQualityDocumentTypes((current) => [
        ...current.filter((item) => item.code !== created.code),
        created,
      ]);
      setSupplierAction(null);
      notify("Zorunlu belge politikası kaydedildi.");
    } catch (error) {
      notify(
        error instanceof Error
          ? error.message
          : "Belge politikası kaydedilemedi.",
      );
    }
  };
  const createSupplier = async () => {
    if (
      !form.name.trim() ||
      !form.legalName.trim() ||
      !form.countryCode.trim() ||
      !form.taxNumber.trim()
    ) {
      notify(
        "Tedarikçi adı, resmî ticari unvan, vergi ülkesi ve vergi numarası zorunludur.",
      );
      return;
    }
    if (
      !form.category.trim() ||
      !form.productGroupCode.trim() ||
      !form.productGroupName.trim()
    ) {
      notify("Ana kategori, ürün grubu kodu ve ürün grubu adı zorunludur.");
      return;
    }
    if (
      Boolean(form.contactName.trim()) !== Boolean(form.contactEmail.trim())
    ) {
      notify(
        "İletişim kişisi eklemek için ad soyad ve e-posta alanlarını birlikte doldurun.",
      );
      return;
    }
    try {
      const created = await apiRequest<SupplierRow>("/api/suppliers", {
        method: "POST",
        body: JSON.stringify({
          name: form.name,
          legalName: form.legalName,
          code: form.code,
          countryCode: form.countryCode,
          taxNumber: form.taxNumber,
          addressLine: form.addressLine,
          status: "DRAFT",
          capabilities: [
            {
              category: form.category,
              productGroupCode: form.productGroupCode,
              productGroupName: form.productGroupName,
            },
            ...(form.includeOtherCategory
              ? [
                {
                  category:
                    form.category === "Hammadde" ? "Ambalaj" : "Hammadde",
                  productGroupCode: form.productGroupCode,
                  productGroupName: form.productGroupName,
                },
              ]
              : []),
          ],
          contacts:
            form.contactName && form.contactEmail
              ? [
                {
                  fullName: form.contactName,
                  email: form.contactEmail,
                  role: form.contactRole,
                  priority: "PRIMARY",
                  emailVerified: false,
                },
              ]
              : [],
        }),
      });
      setRows((current) => [...current, created]);
      setForm({
        name: "",
        legalName: "",
        code: "",
        category: "",
        taxNumber: "",
        countryCode: "TR",
        addressLine: "",
        productGroupCode: "",
        productGroupName: "",
        contactName: "",
        contactEmail: "",
        contactRole: "RFQ",
        includeOtherCategory: false,
      });
      setComposerOpen(false);
      notify(`${created.name} tedarikçi ağına eklendi.`);
    } catch (error) {
      notify(error instanceof Error ? error.message : "Tedarikçi eklenemedi.");
    }
  };
  return {
    user,
    rows,
    query,
    setQuery,
    riskFilter,
    setRiskFilter,
    composerOpen,
    setComposerOpen,
    loading,
    setSelectedSupplierId,
    supplierModalOpenerRef,
    supplierTab,
    setSupplierTab,
    supplierAction,
    setSupplierAction,
    supplierActionPending,
    nextStatus,
    setNextStatus,
    contactForm,
    setContactForm,
    qualityScope,
    setQualityScope,
    qualityDocumentTypes,
    qualityDocumentFile,
    setQualityDocumentFile,
    qualityDocuments,
    eligibility,
    supplierGovernance,
    qualificationCase,
    form,
    setForm,
    visibleRows,
    suppliersWithoutPrimaryContact,
    suppliersNeedingRiskFollowUp,
    selectedSupplier,
    supplierStatusLabels,
    qualificationLabels,
    contactRoleLabels,
    contactRoleDescriptions,
    priorityLabels,
    blockingReasonLabels,
    documentSecurityLabels,
    documentVerificationLabels,
    addContact,
    verifyContact,
    changeSupplierStatus,
    checkEligibility,
    assignCapabilitySite,
    createQualityCase,
    loadQualityCase,
    submitQualityCase,
    decideQualityCase,
    loadQualityDocuments,
    uploadQualityDocument,
    verifyQualityDocument,
    suspendQualityCase,
    createQualityDocumentType,
    createSupplier,
  };
}
